import { clipPath, voiceText, type VoiceLang } from "../domain/voiceWords";
import { audioContext, beep } from "./device";

/**
 * The player's voice (ADR-055): a recorded clip when there is one, the phone's own voice when it
 * has one for the language, a beep otherwise. Clips are the real path — the parents' phones may
 * have no Hindi speech engine at all, and a clip sounds the same on every phone — and
 * `speechSynthesis` is only the interim. `say` never blocks the player and never throws.
 *
 * Clips are fetched the first time a cue needs them and cached by the service worker
 * (`sw.ts`, cache "kaya-voice"); they are never precached, because most people never turn the
 * voice on.
 */

export const VOICE_CACHE = "kaya-voice";

export type VoiceRoute = "clip" | "speech" | "beep" | "off";

interface SpeechLike {
  getVoices(): { lang: string; name: string }[];
  speak(u: unknown): void;
  cancel(): void;
}

export interface VoiceDeps {
  ctx: () => AudioContext | null;
  fetch: (url: string) => Promise<Response>;
  synth: () => SpeechLike | null;
  utterance: (text: string) => { lang: string; rate: number; voice: unknown } | null;
  beep: () => void;
  base: string;
}

/** A BCP 47 tag for a voice: "hi-IN" and "hi_IN" both count as Hindi. */
function voiceFor(synth: SpeechLike, lang: VoiceLang): { lang: string; name: string } | null {
  try {
    return synth.getVoices().find((v) => v.lang.toLowerCase().replace("_", "-").startsWith(lang)) ?? null;
  } catch {
    return null;
  }
}

export interface Voice {
  /** Speaks one cue, part after part; resolves with the route it took. */
  say(ids: readonly string[], lang: VoiceLang, enabled?: boolean): Promise<VoiceRoute>;
  /** Speaks composed words — a walk's "15 minutes · 1,600 steps" — with the phone's voice, a beep
   *  when it has none for the language. No clip: a sentence with a figure in it cannot be one. */
  sayText(text: string, lang: VoiceLang, enabled?: boolean): Promise<VoiceRoute>;
  /** Whether the phone itself can speak this language (its voice list can fill in late). */
  phoneVoice(lang: VoiceLang): boolean;
}

/** The voice over injected browser parts, so the fallback order is testable. */
export function createVoice(d: VoiceDeps): Voice {
  // one fetch per clip per launch, a miss remembered too
  const clips = new Map<string, Promise<AudioBuffer | null>>();

  const load = (lang: VoiceLang, id: string): Promise<AudioBuffer | null> => {
    const url = `${d.base}${clipPath(lang, id)}`;
    let p = clips.get(url);
    if (!p) {
      p = (async () => {
        const ctx = d.ctx();
        if (!ctx) return null;
        const r = await d.fetch(url);
        // a dev server answers a missing path with the app's HTML
        const type = r.headers.get("content-type") ?? "";
        if (!r.ok || !(type.startsWith("audio/") || type === "application/ogg")) return null;
        return await ctx.decodeAudioData(await r.arrayBuffer());
      })().catch(() => null);
      clips.set(url, p);
    }
    return p;
  };

  const play = (bufs: AudioBuffer[]): boolean => {
    const ctx = d.ctx();
    if (!ctx) return false;
    let at = ctx.currentTime + 0.05;
    for (const b of bufs) {
      const src = ctx.createBufferSource();
      src.buffer = b;
      src.connect(ctx.destination);
      src.start(at);
      at += b.duration + 0.12;
    }
    return true;
  };

  const speak = (text: string, lang: VoiceLang): boolean => {
    const synth = d.synth();
    if (!synth) return false;
    const v = voiceFor(synth, lang);
    if (!v) return false;
    const u = d.utterance(text);
    if (!u) return false;
    u.lang = v.lang;
    u.voice = v;
    u.rate = 0.9;
    // Chrome sometimes holds the queue until a cancel; a cue is always about now, never later
    synth.cancel();
    synth.speak(u);
    return true;
  };

  return {
    async say(ids, lang, enabled = true) {
      if (!enabled || !ids.length) return "off";
      try {
        const bufs = await Promise.all(ids.map((id) => load(lang, id)));
        if (bufs.every((b): b is AudioBuffer => !!b) && play(bufs)) return "clip";
      } catch {
        /* fall through to the phone's voice */
      }
      try {
        const text = ids
          .map((id) => voiceText(lang, id))
          .filter((t): t is string => !!t)
          .join(", ");
        if (text && speak(text, lang)) return "speech";
      } catch {
        /* fall through to the beep */
      }
      try {
        d.beep();
      } catch {
        /* nothing left to try */
      }
      return "beep";
    },
    async sayText(text, lang, enabled = true) {
      if (!enabled || !text) return "off";
      try {
        // some engines read the middle dot aloud; the screen keeps it, the voice pauses instead
        if (speak(text.split(" · ").join(", "), lang)) return "speech";
      } catch {
        /* fall through to the beep */
      }
      try {
        d.beep();
      } catch {
        /* nothing left to try */
      }
      return "beep";
    },
    phoneVoice(lang) {
      const synth = d.synth();
      return !!synth && !!voiceFor(synth, lang);
    },
  };
}

function browserSynth(): SpeechLike | null {
  try {
    return typeof window !== "undefined" && "speechSynthesis" in window ? window.speechSynthesis : null;
  } catch {
    return null;
  }
}

export const voice: Voice = createVoice({
  ctx: audioContext,
  fetch: (url) => fetch(url),
  synth: browserSynth,
  utterance: (text) => (typeof SpeechSynthesisUtterance === "undefined" ? null : new SpeechSynthesisUtterance(text)),
  // the long beep: the cue that is always there
  beep: () => beep(880, 600, 0.2),
  base: import.meta.env.BASE_URL ?? "/",
});

/** How many clips for the language this phone has cached; 0 when it cannot tell. */
export async function clipsOnPhone(lang: VoiceLang): Promise<number> {
  try {
    if (typeof caches === "undefined") return 0;
    const c = await caches.open(VOICE_CACHE);
    const keys = await c.keys();
    return keys.filter((k) => new URL(k.url).pathname.includes(`/voice/${lang}/`)).length;
  } catch {
    return 0;
  }
}

/** Calls back when the phone's voice list fills in, which Chrome does late; returns the unsubscribe. */
export function onVoicesChanged(cb: () => void): () => void {
  const synth = browserSynth() as (SpeechLike & EventTarget) | null;
  if (!synth || typeof synth.addEventListener !== "function") return () => undefined;
  synth.addEventListener("voiceschanged", cb);
  return () => synth.removeEventListener("voiceschanged", cb);
}
